import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, TrendingUp, TrendingDown, User, Wallet, Church, BookOpen } from 'lucide-react';
import { supabase, WeeklyLedger, LedgerEntry, User as UserType, EntryType } from '../lib/supabase';
import { formatDate, formatCurrency } from '../lib/dateFormat';
import { translateEntryTypeName } from '../lib/translateEntryType';
import { useLanguage } from '../contexts/LanguageContext';

type LedgerWithDetails = WeeklyLedger & {
  user: UserType;
  entries: (LedgerEntry & { entry_type: EntryType })[];
  total: number;
};

type DashboardProps = {
  onNewLedger: () => void;
};

export default function Dashboard({ onNewLedger }: DashboardProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [ledgers, setLedgers] = useState<LedgerWithDetails[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLedgers();
  }, []);

  const loadLedgers = async () => {
    setLoading(true);
    const { data: ledgersData, error: ledgersError } = await supabase
      .from('weekly_ledgers')
      .select('*')
      .order('date', { ascending: false });

    if (ledgersError || !ledgersData) {
      setLoading(false);
      return;
    }

    const { data: usersData } = await supabase.from('profiles').select('*');
    const { data: entriesData } = await supabase
      .from('ledger_entries')
      .select('*, entry_type:entry_types(*)');

    const ledgersWithDetails = ledgersData.map(ledger => {
      const user = usersData?.find(u => u.id === ledger.created_by);
      const entries = entriesData?.filter(e => e.weekly_ledger_id === ledger.id) || [];
      const total = entries.reduce((sum, entry) => {
        const amount = Number(entry.amount);
        const entryType = entry.entry_type as unknown as EntryType;
        if (entryType?.entry_category === 'expense') {
          return sum - amount;
        }
        return sum + amount;
      }, 0);

      return {
        ...ledger,
        user: user!,
        entries,
        total
      };
    });

    setLedgers(ledgersWithDetails);
    setLoading(false);
  };


  const totalIncome = ledgers.reduce((sum, ledger) => {
    const income = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).entry_category === 'income')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + income;
  }, 0);

  const totalExpenses = ledgers.reduce((sum, ledger) => {
    const expenses = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).entry_category === 'expense')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + expenses;
  }, 0);

  const totalTithe = ledgers.reduce((sum, ledger) => {
    const tithe = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).name.toLowerCase() === 'tithe')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + tithe;
  }, 0);

  const totalFund = ledgers.reduce((sum, ledger) => {
    const fund = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).name.toLowerCase() === 'fund')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + fund;
  }, 0);

  const totalSabbathSchool = ledgers.reduce((sum, ledger) => {
    const sabbathSchool = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).name.toLowerCase() === 'sabbath school')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + sabbathSchool;
  }, 0);

  const currentBalance = totalIncome - totalExpenses;
  const totalChurch = currentBalance - totalTithe - totalSabbathSchool;

  const recentLedgers = ledgers.slice(0, 5);

  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const recentLedgersLast30Days = ledgers.filter(
    ledger => new Date(ledger.date) >= thirtyDaysAgo
  );

  const last30DaysTotal = recentLedgersLast30Days.reduce((sum, ledger) => sum + ledger.total, 0);
  const last30DaysAverage = recentLedgersLast30Days.length > 0
    ? last30DaysTotal / recentLedgersLast30Days.length
    : 0;

  const topEntryTypes = ledgers
    .flatMap(ledger => ledger.entries)
    .reduce((acc, entry) => {
      const entryType = entry.entry_type as unknown as EntryType;
      const key = entryType.name;
      if (!acc[key]) {
        acc[key] = {
          name: key,
          category: entryType.entry_category,
          total: 0,
          count: 0
        };
      }
      acc[key].total += Number(entry.amount);
      acc[key].count += 1;
      return acc;
    }, {} as Record<string, { name: string; category: string; total: number; count: number }>);

  const topIncomeTypes = Object.values(topEntryTypes)
    .filter(type => type.category === 'income')
    .sort((a, b) => b.total - a.total);

  const topExpenseTypes = Object.values(topEntryTypes)
    .filter(type => type.category === 'expense')
    .sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">{t('dashboard.title')}</h1>
          <p className="text-slate-600 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <button
          onClick={onNewLedger}
          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus className="w-5 h-5" />
          {t('dashboard.newWeek')}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-600">{t('dashboard.loading')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  currentBalance >= 0 ? 'bg-emerald-100' : 'bg-red-100'
                }`}>
                  <Wallet className={`w-6 h-6 ${
                    currentBalance >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-slate-600">{t('dashboard.balance')}</p>
                  <p className={`text-2xl font-bold ${
                    currentBalance >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>{formatCurrency(currentBalance)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  totalChurch >= 0 ? 'bg-sky-100' : 'bg-orange-100'
                }`}>
                  <Church className={`w-6 h-6 ${
                    totalChurch >= 0 ? 'text-sky-600' : 'text-orange-600'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-slate-600">{t('statement.totalChurch')}</p>
                  <p className={`text-2xl font-bold ${
                    totalChurch >= 0 ? 'text-sky-600' : 'text-orange-600'
                  }`}>{formatCurrency(totalChurch)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Church className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">{t('dashboard.tithe')}</p>
                  <p className="text-2xl font-bold text-slate-800">{formatCurrency(totalTithe)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">{t('dashboard.sabbathSchool')}</p>
                  <p className="text-2xl font-bold text-slate-800">{formatCurrency(totalSabbathSchool)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">{t('dashboard.income')}</h2>
              </div>

              <div className="mb-6 pb-6 border-b border-slate-200">
                <div className="flex items-baseline justify-between">
                  <span className="text-base text-slate-600">{t('dashboard.totalIncome')}</span>
                  <span className="text-3xl font-bold text-emerald-600">{formatCurrency(totalIncome)}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('dashboard.incomeSources')}</h3>
                <div className="space-y-3">
                  {topIncomeTypes.map((type) => (
                    <div key={type.name} className="flex items-center justify-between">
                      <span className="text-slate-700">{translateEntryTypeName(type.name, language)}</span>
                      <span className="text-lg font-semibold text-emerald-600">{formatCurrency(type.total)}</span>
                    </div>
                  ))}
                  {topIncomeTypes.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-2">{t('dashboard.noIncome')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">{t('dashboard.expense')}</h2>
              </div>

              <div className="mb-6 pb-6 border-b border-slate-200">
                <div className="flex items-baseline justify-between">
                  <span className="text-base text-slate-600">{t('dashboard.totalExpenses')}</span>
                  <span className="text-3xl font-bold text-red-600">{formatCurrency(totalExpenses)}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('dashboard.expenseSources')}</h3>
                <div className="space-y-3">
                  {topExpenseTypes.map((type) => (
                    <div key={type.name} className="flex items-center justify-between">
                      <span className="text-slate-700">{translateEntryTypeName(type.name, language)}</span>
                      <span className="text-lg font-semibold text-red-600">{formatCurrency(type.total)}</span>
                    </div>
                  ))}
                  {topExpenseTypes.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-2">{t('dashboard.noExpenses')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800">{t('dashboard.recentActivity')}</h2>
            </div>
            {recentLedgers.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">{t('dashboard.noLedgers')}</p>
                <p className="text-sm text-slate-500 mt-1">{t('dashboard.noLedgersSubtext')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLedgers.map((ledger) => (
                  <div
                    key={ledger.id}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          {formatDate(ledger.date, "d 'de' MMMM 'de' yyyy", language)}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {ledger.user?.full_name || ledger.user?.email}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        ledger.total >= 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(ledger.total)}
                      </p>
                      <p className="text-xs text-slate-500">{t('dashboard.netTotal')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}